import React from 'react'

const Badge = React.forwardRef(
  (
    {
      children,
      badgeContent,
      color = 'default',
      max,
      showZero = false,
      variant = 'standard',
      sx = {},
      invisible = false,
      className = '',
      ...props
    },
    ref
  ) => {
    // Filter out custom props that shouldn't be passed to DOM
    const { invisible: _, ...domProps } = props

    // Get badge styles based on variant and color
    const getBadgeStyles = () => {
      const baseStyles = {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }

      const badgeStyles = {
        position: 'absolute',
        top: 0,
        right: 0,
        transform: 'scale(1) translate(50%, -50%)',
        transformOrigin: '100% 0%',
        height: '20px',
        minWidth: '20px',
        padding: '0 6px',
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: '20px',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        borderRadius: '10px',
        boxSizing: 'border-box',
        zIndex: 1,
        transition: 'transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
      }

      // Color variations
      const colorStyles = {
        default: {
          backgroundColor: '#e0e0e0',
          color: 'rgba(0, 0, 0, 0.87)'
        },
        primary: {
          backgroundColor: '#1976d2',
          color: '#fff'
        },
        secondary: {
          backgroundColor: '#9c27b0',
          color: '#fff'
        },
        error: {
          backgroundColor: '#d32f2f',
          color: '#fff'
        },
        warning: {
          backgroundColor: '#ed6c02',
          color: '#fff'
        },
        info: {
          backgroundColor: '#0288d1',
          color: '#fff'
        },
        success: {
          backgroundColor: '#2e7d32',
          color: '#fff'
        }
      }

      // Variant styles
      const variantStyles = {
        standard: {
          ...badgeStyles,
          ...colorStyles[color]
        },
        dot: {
          ...badgeStyles,
          height: '8px',
          minWidth: '8px',
          padding: 0,
          borderRadius: '4px',
          ...colorStyles[color]
        }
      }

      return {
        ...baseStyles,
        ...sx
      }
    }

    const getBadgeContentStyles = () => {
      const baseBadgeStyles = {
        position: 'absolute',
        top: 0,
        right: 0,
        transform: 'scale(1) translate(50%, -50%)',
        transformOrigin: '100% 0%',
        height: '20px',
        minWidth: '20px',
        padding: '0 6px',
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: '20px',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        borderRadius: '10px',
        boxSizing: 'border-box',
        zIndex: 1,
        transition: 'transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
      }

      // Color variations
      const colorStyles = {
        default: {
          backgroundColor: '#e0e0e0',
          color: 'rgba(0, 0, 0, 0.87)'
        },
        primary: {
          backgroundColor: '#1976d2',
          color: '#fff'
        },
        secondary: {
          backgroundColor: '#9c27b0',
          color: '#fff'
        },
        error: {
          backgroundColor: '#d32f2f',
          color: '#fff'
        },
        warning: {
          backgroundColor: '#ed6c02',
          color: '#fff'
        },
        info: {
          backgroundColor: '#0288d1',
          color: '#fff'
        },
        success: {
          backgroundColor: '#2e7d32',
          color: '#fff'
        }
      }

      // Variant styles
      const variantStyles = {
        standard: {
          ...baseBadgeStyles,
          ...colorStyles[color]
        },
        dot: {
          ...baseBadgeStyles,
          height: '8px',
          minWidth: '8px',
          padding: 0,
          borderRadius: '4px',
          ...colorStyles[color]
        }
      }

      return variantStyles[variant]
    }

    const styles = getBadgeStyles()
    const badgeStyles = getBadgeContentStyles()

    // Handle badge content
    let displayBadgeContent = badgeContent
    if (
      max !== undefined &&
      typeof badgeContent === 'number' &&
      badgeContent > max
    ) {
      displayBadgeContent = `${max}+`
    }

    // Don't show badge if content is 0 and showZero is false
    if (badgeContent === 0 && !showZero && !invisible) {
      return (
        <span ref={ref} className={className} style={styles} {...domProps}>
          {children}
        </span>
      )
    }

    // Don't show badge if no content
    if (!badgeContent && badgeContent !== 0 && !invisible) {
      return (
        <span ref={ref} className={className} style={styles} {...domProps}>
          {children}
        </span>
      )
    }

    return !invisible ? (
      <span ref={ref} className={className} style={styles} {...domProps}>
        {children}
        <span style={badgeStyles}>
          {variant === 'dot' ? '' : displayBadgeContent}
        </span>
      </span>
    ) : (
      <span ref={ref} className={className} style={styles} {...domProps}>
        {children}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

export default Badge
